import React from "react";
import { Box, Tooltip, Typography } from "@mui/material";

// The exact index/ticker expression each factor is constructed from —
// sourced from factor_map.txt, shown as a hover tooltip on the factor label
// so users can see what actually drives the number.
export const FACTOR_INDEX_MAP = {
  us_eq: "SPX Index",
  eur_eq_rel_us: "SX5E Index - SPX Index",
  jap_eq_rel_us: "NKY Index - SPX Index",
  eq_size_rty_riy: "RTY Index - RIY Index",
  eq_value_vs_growth_rlv_rlg: "RLV Index - RLG Index",
  eq_momentum: "SP500MUP Index - SPX Index",
  vix: "VIX Index",
  vol_6m: "SPX 6M 100 VOL BVOL Index",
  vol_12m: "SPX 1Y 100 VOL BVOL Index",
  ln_vol_6m_1m: "ln(SPX 6M 100 VOL BVOL Index / VIX)",
  ln_vol_12m_1m: "ln(SPX 1Y 100 VOL BVOL Index / VIX)",
  rates_slope_3m_2y: "USGG3M Index - USGG2YR Index",
  rates_slope_5y_2y: "USGG5Y Index - USGG2YR Index",
  rates_slope_10y_2y: "USGG10Y Index - USGG2YR Index",
  rates_slope_30y_2y: "USGG30Y Index - USGG2YR Index",
  usgg2yr: "USGG2YR Index",
  inflation: "USGGT10Y Index",
  hy_credit: "CDX HY CDSI GEN 5Y SPRD Corp",
  ig_credit: "CDX IG CDSI GEN 5Y Corp",
  usd_strength: "DXY Index",
};

export const FACTOR_LABELS = {
  us_eq: "US Equity",
  eur_eq_rel_us: "EUR Equity",
  jap_eq_rel_us: "JPN Equity",
  eq_size_rty_riy: "Size (Small minus Large)",
  eq_value_vs_growth_rlv_rlg: "Value minus Growth",
  eq_momentum: "Momentum",
  vix: "VIX (Vol 1M)",
  vol_6m: "Vol 6M",
  vol_12m: "Vol 12M",
  ln_vol_6m_1m: "Vol 6M (excess Vol 1M)",
  ln_vol_12m_1m: "Vol 12M (excess Vol 1M)",
  rates_slope_3m_2y: "3M Yield",
  rates_slope_5y_2y: "5Y Yield",
  rates_slope_10y_2y: "10Y Yield",
  rates_slope_30y_2y: "30Y Yield",
  usgg2yr: "2Y Yield",
  inflation: "10Y Real Yield",
  hy_credit: "HY Credit",
  ig_credit: "IG Credit",
  usd_strength: "USD Strength",
};

export const FACTOR_ORDER = Object.keys(FACTOR_LABELS);

// The factor-contribution endpoint (used by RiskMonitor's Top 5 Risk Drivers /
// Diversifiers tables) reports `factor` as a human-readable label rather than
// the snake_case key used everywhere else in this file — both forms need to
// resolve to the correct unit here.
const PCT_FACTORS = new Set([
  "us_eq", "eur_eq_rel_us", "jap_eq_rel_us", "eq_size_rty_riy",
  "eq_value_vs_growth_rlv_rlg", "eq_momentum", "vix", "vol_6m", "vol_12m", "ln_vol_6m_1m", "ln_vol_12m_1m", "usd_strength",
  "US Equity Market", "US Small Cap vs Large Cap", "US Value vs Growth", "USD Strength (DXY)",
  "European Equity (rel. US)", "US Equity Momentum", "Japanese Equity (rel. US)", "VIX",
  "Short Maturity Vol*", "Medium Maturity Vol*", "Long Maturity Vol*",
]);
const BPS_X100_FACTORS = new Set([
  "rates_slope_3m_2y", "rates_slope_5y_2y", "rates_slope_10y_2y",
  "rates_slope_30y_2y", "usgg2yr", "inflation",
  "Short Maturity US Rates*", "Medium Maturity US Rates*", "US Breakeven Inflation", "Long Maturity US Rates*",
]);
const BPS_RAW_FACTORS = new Set(["hy_credit", "ig_credit", "HY Credit Spreads", "IG Credit Spreads"]);

export function formatFactorValue(key, raw) {
  if (PCT_FACTORS.has(key)) return `${raw.toFixed(1)}%`;
  if (BPS_X100_FACTORS.has(key)) return `${(raw * 100).toFixed(1)} bps`;
  if (BPS_RAW_FACTORS.has(key)) return `${raw.toFixed(1)} bps`;
  return raw.toFixed(1);
}

// Grouping doubles as the unit grouping above (equity/vol = %, rates/credit = bps),
// so bars within a group are always comparable on the same scale.
const FACTOR_CATEGORIES = [
  { label: "Equity", keys: ["us_eq", "eur_eq_rel_us", "jap_eq_rel_us", "eq_size_rty_riy", "eq_value_vs_growth_rlv_rlg", "eq_momentum"] },
  { label: "Volatility", keys: ["vix", "vol_6m", "vol_12m", "ln_vol_6m_1m", "ln_vol_12m_1m"] },
  { label: "Rates & Inflation", keys: ["rates_slope_3m_2y", "rates_slope_5y_2y", "rates_slope_10y_2y", "rates_slope_30y_2y", "usgg2yr", "inflation"] },
  { label: "Credit", keys: ["hy_credit", "ig_credit"] },
  { label: "FX", keys: ["usd_strength"] },
];

/** Diverging bar centered at 0 — bar grows right (green) for positive, left (red) for negative. */
function DivergingBar({ value, max }) {
  const halfPct = value != null && max > 0 ? Math.min(50, (Math.abs(value) / max) * 50) : 0;
  const isNeg = value < 0;
  return (
    <Box sx={{ position: "relative", width: 44, height: 10, flexShrink: 0 }}>
      <Box sx={{ position: "absolute", left: "50%", top: 0, bottom: 0, width: "1px", bgcolor: "grey.300" }} />
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          height: 4,
          borderRadius: 1,
          transform: "translateY(-50%)",
          bgcolor: isNeg ? "#c53030" : "#006842",
          ...(isNeg
            ? { right: "50%", width: `${halfPct}%` }
            : { left: "50%", width: `${halfPct}%` }),
        }}
      />
    </Box>
  );
}

function CategoryHeader({ label }) {
  return (
    <Typography
      sx={{
        fontSize: "0.62rem",
        fontWeight: 700,
        color: "text.disabled",
        textTransform: "uppercase",
        letterSpacing: "0.07em",
        px: 1.5,
        pt: 1,
        pb: 0.4,
      }}
    >
      {label}
    </Typography>
  );
}

/**
 * `compareFactors` adds a second value column (same table, no second header
 * card) for comparing a different simulation's factors against `factors` —
 * `columnLabel`/`compareColumnLabel` name the two columns (e.g. by sim #).
 */
// Rate-slope factors are stored relative to the 2Y point (e.g. rates_slope_3m_2y =
// USGG3M - USGG2YR). The Simulation Factors table shows the absolute yield instead
// of the bare slope, so usgg2yr is added back in for display — not relevant to the
// Beta table, which is why callers there opt out via showAbsoluteRates={false}.
const RATE_SLOPE_KEYS = new Set([
  "rates_slope_3m_2y", "rates_slope_5y_2y", "rates_slope_10y_2y", "rates_slope_30y_2y",
]);

export function FactorPanel({
  factors,
  compareFactors = null,
  title = "Simulation Factors",
  columnLabel = null,
  compareColumnLabel = null,
  formatValue = formatFactorValue,
  indexMap = FACTOR_INDEX_MAP,
  showAbsoluteRates = true,
}) {
  if (!factors || Object.keys(factors).length === 0) return null;
  const isComparing = !!compareFactors && Object.keys(compareFactors).length > 0;

  const groups = FACTOR_CATEGORIES
    .map((cat) => {
      const entries = cat.keys
        .filter((k) => factors[k] !== undefined || (isComparing && compareFactors[k] !== undefined))
        .map((k) => {
          const addBase = showAbsoluteRates && RATE_SLOPE_KEYS.has(k);
          const rawValue = factors[k];
          const rawCompareValue = isComparing ? compareFactors[k] : undefined;
          const value = addBase && rawValue != null && factors.usgg2yr != null
            ? rawValue + factors.usgg2yr
            : rawValue;
          const compareValue = addBase && rawCompareValue != null && compareFactors?.usgg2yr != null
            ? rawCompareValue + compareFactors.usgg2yr
            : rawCompareValue;
          return {
            key: k,
            label: FACTOR_LABELS[k],
            value,
            compareValue,
          };
        });
      if (entries.length === 0) return null;
      const max = Math.max(
        1e-9,
        ...entries.map((e) => Math.abs(e.value ?? 0)),
        ...(isComparing ? entries.map((e) => Math.abs(e.compareValue ?? 0)) : []),
      );
      return { label: cat.label, entries, max };
    })
    .filter(Boolean);

  const valueCellSx = {
    fontSize: "0.75rem",
    fontWeight: 700,
    fontFamily: "monospace",
    width: isComparing ? 58 : 62,
    textAlign: "right",
    flexShrink: 0,
  };

  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        borderRadius: 1,
        boxShadow: "0 1px 3px rgba(0,0,0,0.07)",
        overflow: "hidden",
      }}
    >
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: "0.72rem",
          color: "#006842",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          py: 0.6,
          px: 1.5,
          bgcolor: "rgba(0,104,66,0.06)",
          borderBottom: "2px solid #006842",
        }}
      >
        {title}
      </Typography>

      {isComparing && (columnLabel || compareColumnLabel) && (
        <Box sx={{ display: "flex", alignItems: "center", px: 1.5, pt: 0.6 }}>
          <Box sx={{ flex: 1 }} />
          <Typography sx={{ ...valueCellSx, fontWeight: 700, fontSize: "0.62rem", color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.04em" }}>
            {columnLabel}
          </Typography>
          <Typography sx={{ ...valueCellSx, fontWeight: 700, fontSize: "0.62rem", color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.04em" }}>
            {compareColumnLabel}
          </Typography>
        </Box>
      )}

      {groups.map((group, gIdx) => (
        <Box key={group.label}>
          <CategoryHeader label={group.label} />
          {group.entries.map(({ key, label, value, compareValue }, idx) => {
            const isNeg = value < 0;
            const isCompareNeg = compareValue < 0;
            return (
              <Box
                key={key}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.5,
                  py: 0.45,
                  bgcolor: idx % 2 === 0 ? "transparent" : "rgba(0,0,0,0.015)",
                  "&:hover": { bgcolor: "rgba(0,104,66,0.06)" },
                }}
              >
                <Tooltip title={indexMap[key] || "No index mapping available"} arrow placement="top">
                  <Typography
                    sx={{
                      fontSize: "0.74rem",
                      fontWeight: 500,
                      color: "text.primary",
                      flex: 1,
                      minWidth: 0,
                      lineHeight: 1.25,
                      cursor: "help",
                      textDecoration: "underline dotted",
                      textUnderlineOffset: 2,
                      textDecorationColor: "rgba(0,0,0,0.25)",
                    }}
                  >
                    {label}
                  </Typography>
                </Tooltip>
                {!isComparing && <DivergingBar value={value} max={group.max} />}
                <Typography sx={{ ...valueCellSx, color: value == null ? "text.disabled" : isNeg ? "#c53030" : "#006842" }}>
                  {value != null ? formatValue(key, value) : "—"}
                </Typography>
                {isComparing && (
                  <Typography sx={{ ...valueCellSx, color: compareValue == null ? "text.disabled" : isCompareNeg ? "#c53030" : "#006842" }}>
                    {compareValue != null ? formatValue(key, compareValue) : "—"}
                  </Typography>
                )}
              </Box>
            );
          })}
          {gIdx < groups.length - 1 && (
            <Box sx={{ borderBottom: "1px solid #f0f0f0", mx: 1.5 }} />
          )}
        </Box>
      ))}
    </Box>
  );
}

export default FactorPanel;
